import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import authInstance from "data/firestore/auth";
import CurrentSession from "data/userSession";
import useCustomSnackbar from "hooks/customSnackbar";

// AuthGuard renders this for signed-in users whose email isn't verified yet
// (password sign-ups). Google/Apple users are verified by the provider and
// never see it.
const notifyAuthChange = () =>
  window.dispatchEvent(new CustomEvent("authStateChanged"));

const VerifyEmail = () => {
  const { enqueueSnackbar } = useCustomSnackbar();
  const [checking, setChecking] = useState(false);

  const handleRecheck = async () => {
    setChecking(true);
    try {
      const verified = await authInstance.reloadUser();
      if (verified) {
        enqueueSnackbar("Email verified!", { variant: "success" });
        notifyAuthChange(); // AuthGuard re-checks and renders the app
      } else {
        enqueueSnackbar("Not verified yet. Check your inbox (and spam).", {
          variant: "info",
        });
      }
    } catch {
      enqueueSnackbar("Couldn't check verification. Try again.", {
        variant: "error",
      });
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    try {
      await authInstance.sendVerificationEmail();
      enqueueSnackbar("Verification email sent.", { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't send email. Try again shortly.", {
        variant: "error",
      });
    }
  };

  const handleSignOut = async () => {
    await CurrentSession.SignOut();
    notifyAuthChange();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        minHeight: "100vh",
        p: 3,
        textAlign: "center",
      }}
    >
      <Typography variant="h4">Verify your email</Typography>
      <Typography sx={{ maxWidth: 420 }} color="text.secondary">
        We sent a verification link to your email. Click it, then tap “I’ve
        verified” below.
      </Typography>
      <Button variant="contained" onClick={handleRecheck} disabled={checking}>
        {checking ? "Checking…" : "I’ve verified"}
      </Button>
      <Button variant="text" onClick={handleResend}>
        Resend email
      </Button>
      <Button variant="text" color="inherit" onClick={handleSignOut}>
        Sign out
      </Button>
    </Box>
  );
};

export default VerifyEmail;
